import { useState } from "react";
import { ChevronDown } from "lucide-react";
import { cn } from "@/lib/cn";
import { MobileFilterSheet, type OpcaoDeFiltro } from "./MobileFilterSheet";

type Props = {
  statusOpcoes: OpcaoDeFiltro[];
  statusSelecionados: string[];
  onStatusChange: (valores: string[]) => void;

  retrabalhoAtivo: boolean;
  onRetrabalhoChange: (ativo: boolean) => void;

  estagioOpcoes: OpcaoDeFiltro[];
  estagiosSelecionados: string[];
  onEstagiosChange: (valores: string[]) => void;
};

const RETRABALHO_ABERTO = "aberto";

/**
 * Faixa de filtros do celular: rótulo FILTROS e três controles — Status, Retrabalho e Estágio.
 *
 * Substitui o botão genérico "Filtros" que abria um painel: no celular aquele painel empilhava
 * dezenas de chips e empurrava o conteúdo pra baixo da dobra. Aqui cada filtro é um controle com
 * o próprio contador, e a escolha acontece numa folha inferior.
 *
 * O rótulo do filtro fica SEMPRE visível — "Status 2", nunca "Em produção" — porque trocar o nome
 * pelo primeiro valor escolhido esconde que existem outros marcados.
 *
 * Nenhuma regra de filtragem nova: os três continuam alimentando o mesmo estado da página.
 */
export function MobileProductionFilters({
  statusOpcoes,
  statusSelecionados,
  onStatusChange,
  retrabalhoAtivo,
  onRetrabalhoChange,
  estagioOpcoes,
  estagiosSelecionados,
  onEstagiosChange,
}: Props) {
  const [aberta, setAberta] = useState<"status" | "retrabalho" | "estagio" | null>(null);

  const chips = [
    { chave: "status" as const, rotulo: "Status", quantos: statusSelecionados.length },
    { chave: "retrabalho" as const, rotulo: "Retrabalho", quantos: retrabalhoAtivo ? 1 : 0 },
    { chave: "estagio" as const, rotulo: "Estágio", quantos: estagiosSelecionados.length },
  ];

  return (
    <div className="mb-3 md:hidden">
      <p className="mb-1.5 text-[11px] font-semibold uppercase tracking-wide text-zinc-400 dark:text-muted-foreground">
        Filtros
      </p>

      {/* Os três dividem a largura em partes iguais; a fonte se ajusta com `clamp()` pra caberem
          sem rolagem, do mesmo jeito das segmentações de Pedidos e Parceiros. */}
      <div className="flex items-center gap-2">
        {chips.map(({ chave, rotulo, quantos }) => (
          <button
            key={chave}
            type="button"
            onClick={() => setAberta(chave)}
            aria-haspopup="dialog"
            className={cn(
              "inline-flex h-11 min-w-0 flex-1 basis-0 items-center justify-center gap-1 rounded-md border px-1.5 text-[clamp(10.5px,3vw,13px)] font-semibold transition-colors",
              quantos > 0
                ? "border-brand-200 bg-brand-50 text-brand-700 dark:border-primary/30 dark:bg-primary/15 dark:text-primary"
                : "border-zinc-200 bg-white text-zinc-700 dark:border-border dark:bg-card dark:text-foreground",
            )}
          >
            <span className="truncate">{rotulo}</span>
            {quantos > 0 && (
              <span className="inline-flex h-4 min-w-[16px] items-center justify-center rounded-full bg-brand-600 px-1 text-[10px] font-bold text-white">
                {quantos}
              </span>
            )}
            <ChevronDown className="h-3.5 w-3.5 shrink-0 opacity-60" />
          </button>
        ))}
      </div>

      <MobileFilterSheet
        aberto={aberta === "status"}
        titulo="Status"
        opcoes={statusOpcoes}
        selecionados={statusSelecionados}
        onFechar={() => setAberta(null)}
        onAplicar={onStatusChange}
      />

      {/* Retrabalho é binário no estado da página (`reworkOnly`). Aparece como opção única pra não
          inventar regra nova de filtragem só por causa da forma do controle. */}
      <MobileFilterSheet
        aberto={aberta === "retrabalho"}
        titulo="Retrabalho"
        opcoes={[{ valor: RETRABALHO_ABERTO, rotulo: "Com retrabalho aberto", cor: "#ef4444" }]}
        selecionados={retrabalhoAtivo ? [RETRABALHO_ABERTO] : []}
        onFechar={() => setAberta(null)}
        onAplicar={(v) => onRetrabalhoChange(v.includes(RETRABALHO_ABERTO))}
      />

      <MobileFilterSheet
        aberto={aberta === "estagio"}
        titulo="Estágio"
        opcoes={estagioOpcoes}
        selecionados={estagiosSelecionados}
        comBusca={estagioOpcoes.length > 8}
        onFechar={() => setAberta(null)}
        onAplicar={onEstagiosChange}
      />
    </div>
  );
}
